import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Post, ThreatLevel } from '../types';
import { DateFilter, DateRange } from '../components/DateFilter';
import {
  Search,
  Filter,
  ExternalLink,
  User,
  RefreshCw,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Shield,
  Activity,
  Clock,
  Hash,
  Globe,
  Zap,
  PieChart as PieChartIcon,
  Users,
  Database,
  Calendar,
  Eye,
  Brain,
  Bell
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatPostIdForDisplay } from '../utils/id-normalization';
import { formatAbsoluteTime } from '../utils/date-formatter';
import { RelevanceAnalysisCard } from '../components/RelevanceAnalysisCard';
import { Pagination } from '../components/Pagination';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Area,
  AreaChart,
  ComposedChart,
  ScatterChart,
  Scatter
} from 'recharts';

const THREAT_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  unknown: '#6b7280'
};

export function Posts() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [threatFilter, setThreatFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [lastLoaded, setLastLoaded] = useState<string>('');
  const [showDashboard, setShowDashboard] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [allPostsForDashboard, setAllPostsForDashboard] = useState<Post[]>([]);
  const [threatCounts, setThreatCounts] = useState<{
    critical: number;
    high: number;
    medium: number;
    low: number;
    unknown: number;
  }>({
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    unknown: 0
  });
  const [expandedRelevancePost, setExpandedRelevancePost] = useState<string | null>(null);
  const [rssAppFeeds, setRssAppFeeds] = useState<Array<{
    id: string;
    source_feed: string;
    post_count: number;
    name: string;
    url: string;
    description: string;
    last_synced: string | null;
  }>>([]);
  const [rssFeeds, setRssFeeds] = useState<Array<{
    id: string;
    source_feed: string;
    post_count: number;
    name: string;
    url: string;
    description: string;
    last_synced: string | null;
  }>>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    period: 'all'
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadPosts();
    loadDashboardData();
  }, [currentPage, threatFilter, dateRange]);

  // Load RSS feeds data for feed name mapping
  useEffect(() => {
    const loadFeedsData = async () => {
      try {
        const [rssAppResponse, rssResponse] = await Promise.all([
          api.getRssAppFeeds(),
          api.getRssFeeds()
        ]);

        if (rssAppResponse.feeds) {
          setRssAppFeeds(rssAppResponse.feeds);
        }
        if (rssResponse.feeds) {
          setRssFeeds(rssResponse.feeds);
        }
      } catch (error) {
        console.error('Failed to load feeds data:', error);
      }
    };

    loadFeedsData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }

      // Calculate stats range based on date selection
      let statsRange = '7d';
      if (dateRange.period === '7days') {
        statsRange = '7d';
      } else if (dateRange.period === '30days') {
        statsRange = '30d';
      } else if (dateRange.period === 'all') {
        statsRange = 'all';
      }

      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;

      // Load more posts for dashboard analytics
      const [metricsData, statsData, allPostsData] = await Promise.all([
        api.getMetrics(apiDateParams),
        api.getStats(statsRange, apiDateParams),
        api.getPosts({ limit: 100, order_by: 'created_at', ...apiDateParams })
      ]);

      setMetrics(metricsData);
      setStats(statsData);
      setAllPostsForDashboard(allPostsData.posts);

      // Process threat counts from metrics
      if (metricsData && metricsData.threat_distribution) {
        const counts = {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0,
          unknown: 0
        };

        metricsData.threat_distribution.forEach((item: any) => {
          if (counts.hasOwnProperty(item.threat_level)) {
            counts[item.threat_level as keyof typeof counts] = item.count;
          }
        });

        setThreatCounts(counts);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const loadPosts = async () => {
    try {
      setLoading(true);

      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }

      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;

      const response = await api.getPosts({
        page: currentPage,
        limit: 20,
        threat_level: threatFilter || undefined,
        order_by: 'created_at',
        ...apiDateParams
      });

      setPosts(response.posts);
      setTotalPages(response.pagination.pages);
      setTotalPosts(response.pagination.total);
      setLastLoaded(new Date().toLocaleString());
      console.log('Posts loaded:', response.posts.length, 'posts at', new Date().toLocaleString());
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadPosts();
      return;
    }

    try {
      setLoading(true);
      const response = await api.searchPosts(searchQuery);
      setPosts(response.posts);
      setTotalPages(1);
      setCurrentPage(1);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getThreatBadgeClass = (level: ThreatLevel) => {
    const baseClass = 'threat-badge ';
    switch (level) {
      case 'critical': return baseClass + 'threat-critical';
      case 'high': return baseClass + 'threat-high';
      case 'medium': return baseClass + 'threat-medium';
      case 'low': return baseClass + 'threat-low';
      default: return baseClass + 'threat-unknown';
    }
  };

  // Function to get readable name for a source feed
  const getFeedDisplayName = (sourceFeed: string): string => {
    // Check RSS.app feeds first
    const rssAppFeed = rssAppFeeds.find(feed => feed.source_feed === sourceFeed);
    if (rssAppFeed) {
      return rssAppFeed.name || sourceFeed.replace('rss-app-', '');
    }

    // Check regular RSS feeds
    const rssFeed = rssFeeds.find(feed => feed.source_feed === sourceFeed);
    if (rssFeed) {
      return rssFeed.name || sourceFeed;
    }

    // Fallback to cleaning up the source feed name
    if (sourceFeed.startsWith('rss-app-')) {
      return sourceFeed.replace('rss-app-', '').substring(0, 20) + '...';
    }

    return sourceFeed;
  };

  // Calculate dashboard metrics
  const dashboardMetrics = {
    threatDistribution: (() => {
      const grouped = allPostsForDashboard.reduce((acc, post) => {
        const level = post.threat_level || 'unknown';
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(grouped).map(([level, count]) => ({
        name: level,
        value: count,
        color: THREAT_COLORS[level as keyof typeof THREAT_COLORS] || THREAT_COLORS.unknown
      }));
    })(),

    sourceFeedDistribution: (() => {
      const grouped = allPostsForDashboard.reduce((acc, post) => {
        acc[post.source_feed] = (acc[post.source_feed] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(grouped)
        .map(([feed, count]) => ({
          feed: getFeedDisplayName(feed),
          originalFeed: feed,
          count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    })(),

    topPosters: (() => {
      const grouped = allPostsForDashboard.reduce((acc, post) => {
        acc[post.username] = (acc[post.username] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return Object.entries(grouped)
        .map(([username, count]) => ({ username, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    })(),

    hourlyActivity: (() => {
      const hours = Array.from({ length: 24 }, (_, i) => i);
      const grouped = allPostsForDashboard.reduce((acc, post) => {
        const hour = new Date(post.created_at).getHours();
        acc[hour] = (acc[hour] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);

      return hours.map(hour => ({
        hour: `${hour}:00`,
        posts: grouped[hour] || 0
      }));
    })(),

    relevanceDistribution: (() => {
      const ranges = [
        { name: '90-100%', min: 0.9, max: 1.0 },
        { name: '75-89%', min: 0.75, max: 0.9 },
        { name: '50-74%', min: 0.5, max: 0.75 },
        { name: '<50%', min: 0, max: 0.5 }
      ];

      return ranges.map(range => ({
        name: range.name,
        value: allPostsForDashboard.filter(p =>
          p.relevance_score >= range.min && p.relevance_score < range.max
        ).length
      }));
    })(),

    timelineData: (() => {
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();

      const postsByDate = allPostsForDashboard.reduce((acc, post) => {
        const date = new Date(post.created_at).toISOString().split('T')[0];
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return last7Days.map(date => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        posts: postsByDate[date] || 0
      }));
    })()
  };

  const averageRelevance = allPostsForDashboard.length > 0
    ? (allPostsForDashboard.reduce((sum, post) => sum + (post.relevance_score || 0), 0) / allPostsForDashboard.length * 100).toFixed(1)
    : '0';

  // Use threat counts from metrics API instead of calculating from limited posts
  const criticalCount = threatCounts.critical;
  const highCount = threatCounts.high;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Posts</h2>
          {lastLoaded && (
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {lastLoaded} | Total posts: {totalPosts} | Sorted by: created_at (newest first)
            </p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <DateFilter
            onChange={setDateRange}
            initialValue={dateRange}
          />
          <button
            onClick={() => setShowDashboard(!showDashboard)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            <span>{showDashboard ? 'Hide' : 'Show'} Dashboard</span>
          </button>
          <button
            onClick={() => {
              loadPosts();
              loadDashboardData();
            }}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Dashboard Section */}
      {showDashboard && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard
              title="Total Posts"
              value={totalPosts}
              icon={Database}
              color="blue"
              trend={stats?.trend_data ? `+${stats.trend_data.slice(-1)[0]?.post_count || 0} today` : ''}
            />
            <StatCard
              title="Critical Threats"
              value={criticalCount}
              icon={AlertTriangle}
              color="red"
              percentage={totalPosts > 0 ? Math.round(criticalCount / totalPosts * 100) : 0}
            />
            <StatCard
              title="High Threats"
              value={highCount}
              icon={Shield}
              color="orange"
              percentage={totalPosts > 0 ? Math.round(highCount / totalPosts * 100) : 0}
            />
            <StatCard
              title="Active Users"
              value={new Set(allPostsForDashboard.map(p => p.username)).size}
              icon={Users}
              color="green"
              subtitle="unique posters"
            />
            <StatCard
              title="Avg Relevance"
              value={`${averageRelevance}%`}
              icon={Zap}
              color="purple"
              subtitle="content relevance"
            />
          </div>

          {/* Charts Grid - First Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Threat Distribution */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Threat Level Distribution</h3>
                  <p className="text-sm text-gray-500">Distribution of threat severity across all posts</p>
                </div>
                <PieChartIcon className="h-6 w-6 text-purple-500" />
              </div>
              {dashboardMetrics.threatDistribution.length > 0 ? (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={dashboardMetrics.threatDistribution}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                      >
                        {dashboardMetrics.threatDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          `${value} posts`,
                          name.charAt(0).toUpperCase() + name.slice(1)
                        ]}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No threat data available
                </div>
              )}
            </div>

            {/* Activity Timeline */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Post Activity Timeline</h3>
                  <p className="text-sm text-gray-500">Daily post volume over the last 7 days</p>
                </div>
                <TrendingUp className="h-6 w-6 text-blue-500" />
              </div>
              {dashboardMetrics.timelineData.length > 0 ? (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <AreaChart data={dashboardMetrics.timelineData}>
                      <defs>
                        <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Area type="monotone" dataKey="posts" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPosts)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No timeline data available
                </div>
              )}
            </div>
          </div>

          {/* Charts Grid - Second Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Hourly Activity Heatmap */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">24-Hour Activity Pattern</h3>
                  <p className="text-sm text-gray-500">Post frequency by hour</p>
                </div>
                <Clock className="h-6 w-6 text-orange-500" />
              </div>
              {dashboardMetrics.hourlyActivity.length > 0 ? (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={dashboardMetrics.hourlyActivity}>
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Bar dataKey="posts" fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No activity data available
                </div>
              )}
            </div>

            {/* Source Feed Distribution */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Top Source Feeds</h3>
                  <p className="text-sm text-gray-500">Most active feed sources</p>
                </div>
                <Globe className="h-6 w-6 text-green-500" />
              </div>
              <div className="space-y-3">
                {dashboardMetrics.sourceFeedDistribution.length > 0 ? (
                  dashboardMetrics.sourceFeedDistribution.map((source, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${index === 0 ? 'bg-green-500' :
                            index === 1 ? 'bg-green-400' :
                              'bg-green-300'
                          }`}>
                          {index + 1}
                        </div>
                        <span className="font-medium text-gray-900" title={source.originalFeed || source.feed}>
                          {source.feed}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-600">{source.count} posts</span>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No feed data available
                  </div>
                )}
              </div>
            </div>

            {/* Relevance Score Distribution */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Relevance Score Distribution</h3>
                  <p className="text-sm text-gray-500">Content relevance ranges</p>
                </div>
                <Zap className="h-6 w-6 text-purple-500" />
              </div>
              {dashboardMetrics.relevanceDistribution.some(d => d.value > 0) ? (
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={dashboardMetrics.relevanceDistribution}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  No relevance data available
                </div>
              )}
            </div>
          </div>

          {/* Top Posters */}
          <div className="bg-white p-6 rounded-lg shadow-lg border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Top Contributors</h3>
                <p className="text-sm text-gray-500">Most active users by post count</p>
              </div>
              <Users className="h-6 w-6 text-blue-500" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {dashboardMetrics.topPosters.slice(0, 10).map((poster, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${index === 0 ? 'bg-yellow-500' :
                        index === 1 ? 'bg-gray-400' :
                          index === 2 ? 'bg-orange-600' :
                            'bg-blue-500'
                      }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">@{poster.username}</p>
                      <p className="text-xs text-gray-500">{poster.count} posts</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <hr className="border-gray-200" />

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by post ID, content, or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Threat Level Filter */}
          <div className="sm:w-48">
            <select
              value={threatFilter}
              onChange={(e) => setThreatFilter(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Threat Levels</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>

          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Search
          </button>
        </div>
      </div>

      {/* Posts List */}
      <div className="bg-white shadow rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No posts found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {posts.map((post) => (
              <div key={post.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <p className="text-sm font-medium text-gray-900">
                        @{post.username}
                      </p>
                      <span className={`${getThreatBadgeClass(post.threat_level)} flex items-center gap-1`}>
                        <Shield className="h-3 w-3" />
                        {post.threat_level.toUpperCase()}
                      </span>

                      {/* Post ID - Clickable */}
                      <span
                        className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/analyzed-posts?highlight=${post.id}`);
                        }}
                        title="Click to view post details"
                      >
                        Post: {formatPostIdForDisplay(post.id)}
                      </span>

                      {/* Alert ID - Clickable if exists */}
                      {post.latest_alert_id && (
                        <span
                          className="text-xs font-mono text-red-600 bg-red-50 px-2 py-0.5 rounded cursor-pointer hover:bg-red-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            const params = new URLSearchParams();
                            params.append('alert_id', post.latest_alert_id!);
                            navigate(`/alerts?${params.toString()}`);
                          }}
                          title="Click to view alert details"
                        >
                          Alert: {post.latest_alert_id}
                        </span>
                      )}

                      {/* Analysis ID - Clickable if exists */}
                      {post.analysis_id && (
                        <span
                          className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded cursor-pointer hover:bg-purple-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/analysis/${post.analysis_id}`);
                          }}
                          title="Click to view analysis details"
                        >
                          Analysis: {post.analysis_id}
                        </span>
                      )}
                    </div>

                    <div className="mb-3">
                      <p className="text-gray-900 text-sm leading-relaxed">
                        {post.content}
                      </p>
                    </div>

                    {/* Relevance Analysis Section */}
                    <div className="mb-4">
                      <RelevanceAnalysisCard
                        postId={post.id}
                        relevanceScore={post.relevance_score}
                        expanded={expandedRelevancePost === post.id}
                        onToggle={() => setExpandedRelevancePost(
                          expandedRelevancePost === post.id ? null : post.id
                        )}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>{formatAbsoluteTime(post.created_at)}</span>
                        <span>Feed: {post.source_feed}</span>
                      </div>

                      <div className="flex items-center space-x-3">
                        {/* Analysis Link */}
                        {post.analyzed_at && (
                          <button
                            onClick={() => navigate(`/analyzed-posts?highlight=${post.id}`)}
                            className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800 font-medium"
                          >
                            <Brain className="h-4 w-4 mr-1" />
                            View Analysis
                          </button>
                        )}

                        {/* Alert Link */}
                        {post.alert_count && post.alert_count > 0 && (
                          <button
                            onClick={() => {
                              const params = new URLSearchParams({ post_id: post.id });
                              if (post.latest_alert_id) {
                                params.set('highlight', post.latest_alert_id);
                              }
                              navigate(`/alerts?${params.toString()}`);
                            }}
                            className="inline-flex items-center text-sm text-orange-600 hover:text-orange-800 font-medium"
                          >
                            <Bell className="h-4 w-4 mr-1" />
                            View Alert{post.alert_count > 1 ? 's' : ''} ({post.alert_count})
                          </button>
                        )}

                        {/* Original Link */}
                        {post.url && (
                          <a
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View Original
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'blue' | 'red' | 'orange' | 'green' | 'purple';
  trend?: string;
  percentage?: number;
  subtitle?: string;
}

function StatCard({ title, value, icon: Icon, color, trend, percentage, subtitle }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-500 text-white',
    red: 'bg-red-500 text-white',
    orange: 'bg-orange-500 text-white',
    green: 'bg-green-500 text-white',
    purple: 'bg-purple-500 text-white'
  };

  return (
    <div className="bg-white overflow-hidden shadow-lg rounded-lg border">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-bold text-gray-900">
                  {value}
                </div>
                {percentage !== undefined && (
                  <span className="ml-2 text-sm font-medium text-gray-500">
                    ({percentage}%)
                  </span>
                )}
              </dd>
              {(trend || subtitle) && (
                <dd className="mt-1">
                  {trend && (
                    <span className="text-xs font-medium text-green-600">
                      {trend}
                    </span>
                  )}
                  {subtitle && (
                    <span className="text-xs text-gray-500">
                      {subtitle}
                    </span>
                  )}
                </dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}